import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    FormControl,
    FormControlLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    Slider,
    Typography,
    useTheme,
    TextField
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { clearErrors, getProducts } from '../../actions/productAction';
import Loader from '../Layouts/Loader';
import MinCategory from '../Layouts/MinCategory';
import Product from './Product';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import { getCategories } from '../../actions/categoryAction';
import MetaData from '../Layouts/MetaData';
import { useLocation } from 'react-router-dom';
import Pagination from '@mui/material/Pagination';

const Products = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const params = useParams();
    const location = useLocation();
    const theme = useTheme();

    const [price, setPrice] = useState([0, 10000000]);
    const [priceInput, setPriceInput] = useState(['0', 'Unlimited']);
    const [category, setCategory] = useState(location.search ? location.search.split("=")[1] : "");
    const [ratings, setRatings] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedCategories, setExpandedCategories] = useState([]);

    const { products, loading, error, productsCount, resultPerPage, filteredProductsCount } = useSelector((state) => state.products);
    const { categories } = useSelector((state) => state.categories);
    const keyword = params.keyword;

    const priceHandler = (e, newPrice) => {
        setPrice(newPrice);
    };

    const handlePriceInputChange = (e, index) => {
        const newPriceInput = [...priceInput];
        newPriceInput[index] = e.target.value;
        setPriceInput(newPriceInput);
    };

    const handlePriceInputBlur = () => {
        let min = parseInt(priceInput[0], 10);
        let max;

        if (priceInput[1].toLowerCase() === 'unlimited' || priceInput[1] === '') {
            max = 10000000;
        } else {
            max = parseInt(priceInput[1], 10);
        }

        if (isNaN(min)) min = 0;
        if (isNaN(max)) max = 10000000;

        if (min < 0) min = 0;
        if (max > 10000000) max = 10000000;
        if (min > max) {
            min = max;
        }

        setPrice([min, max]);
    };

    const clearFilters = () => {
        setPrice([0, 10000000]);
        setCategory("");
        setRatings(0);
    };

    useEffect(() => {
        setPriceInput([
            price[0].toString(),
            price[1] === 10000000 ? 'Unlimited' : price[1].toString()
        ]);
    }, [price]);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        dispatch(getProducts(keyword, category, price, ratings, currentPage));
        dispatch(getCategories());
    }, [dispatch, keyword, category, price, ratings, currentPage, error, enqueueSnackbar]);

    return (
        <>
            <MetaData title="All Products - AmritLok" />
            <MinCategory />
            <main style={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }}>
                <Grid container spacing={3} sx={{ p: 3 }}>
                    <Grid item xs={12} sm={3}>
                        <Paper elevation={3} sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>Filters</Typography>
                            <Button variant="outlined" onClick={clearFilters} fullWidth sx={{ mb: 2 }}>
                                Clear All
                            </Button>
                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>Price</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Slider
                                        value={price}
                                        onChange={priceHandler}
                                        valueLabelDisplay="auto"
                                        valueLabelFormat={(value) => value === 10000000 ? 'Unlimited' : `₹${value}`}
                                        min={0}
                                        max={10000000}
                                        sx={{ mt: 2 }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <TextField
                                            label="Min"
                                            variant="outlined"
                                            size="small"
                                            value={priceInput[0]}
                                            onChange={(e) => handlePriceInputChange(e, 0)}
                                            onBlur={handlePriceInputBlur}
                                        />
                                        <Typography sx={{ mx: 1 }}>-</Typography>
                                        <TextField
                                            label="Max"
                                            variant="outlined"
                                            size="small"
                                            value={priceInput[1]}
                                            onChange={(e) => handlePriceInputChange(e, 1)}
                                            onBlur={handlePriceInputBlur}
                                        />
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>Category</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={category}
                                            onChange={(e) => setCategory(e.target.value)}
                                        >
                                            {categories && categories.map((cat) => (
                                                <div key={cat._id}>
                                                    <FormControlLabel
                                                        value={cat.name}
                                                        control={<Radio />}
                                                        label={cat.name}
                                                    />
                                                    {cat.children && cat.children.length > 0 && (
                                                        <Button
                                                            size="small"
                                                            onClick={() => {
                                                                const newExpanded = [...expandedCategories];
                                                                const index = newExpanded.indexOf(cat._id);
                                                                if (index > -1) {
                                                                    newExpanded.splice(index, 1);
                                                                } else {
                                                                    newExpanded.push(cat._id);
                                                                }
                                                                setExpandedCategories(newExpanded);
                                                            }}
                                                        >
                                                            {expandedCategories.includes(cat._id) ? '-' : '+'}
                                                        </Button>
                                                    )}
                                                    {expandedCategories.includes(cat._id) && (
                                                        <div style={{ marginLeft: '20px' }}>
                                                            {cat.children.map((child) => (
                                                                <FormControlLabel
                                                                    key={child._id}
                                                                    value={child.name}
                                                                    control={<Radio />}
                                                                    label={child.name}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </RadioGroup>
                                    </FormControl>
                                </AccordionDetails>
                            </Accordion>
                            <Accordion defaultExpanded>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>Ratings</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <RadioGroup
                                        value={ratings}
                                        onChange={(e) => setRatings(e.target.value)}
                                    >
                                        {[4, 3, 2, 1].map((r) => (
                                            <FormControlLabel
                                                key={r}
                                                value={r}
                                                control={<Radio />}
                                                label={
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {r} <StarIcon sx={{ fontSize: '1rem', ml: 0.5 }} /> & above
                                                    </Box>
                                                }
                                            />
                                        ))}
                                    </RadioGroup>
                                </AccordionDetails>
                            </Accordion>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                        {loading ? (
                            <Loader />
                        ) : (
                            <>
                                {products?.length === 0 ? (
                                    <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                                        <img draggable="false" className="w-1/2 h-44 object-contain" src="https://static-assets-web.flixcart.com/www/linchpin/fk-cp-zion/img/error-no-search-results_2353c5.png" alt="Search Not Found" />
                                        <Typography variant="h5" gutterBottom>
                                            Sorry, no results found!
                                        </Typography>
                                        <Typography>
                                            Please check the spelling or try searching for something else
                                        </Typography>
                                    </Paper>
                                ) : (
                                    <Grid container spacing={2}>
                                        {products?.map((product) => (
                                            <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
                                                <Product {...product} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                                {filteredProductsCount > resultPerPage && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                        <Pagination
                                            count={Math.ceil(filteredProductsCount / resultPerPage)}
                                            page={currentPage}
                                            onChange={(e, val) => setCurrentPage(val)}
                                            color="primary"
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>
            </main>
        </>
    );
};

export default Products;